import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { setServio } from "../../../../../../redux/servioSlice";
import { RootState } from "../../../../../../redux/store";
import { DEFAULT_MAIN_COLOR } from "@constants/index";

import { Container, SectionTitle } from "./styled";
import { LanguagesCard } from "./LanguagesCard";
import { DiversState, LANGUAGES } from "./types";

const DEFAULT_ENABLED = LANGUAGES.reduce(
    (acc, lang) => ({ ...acc, [lang.code]: true }),
    {} as Record<string, boolean>
);

const DEFAULT_LANGUAGE = "fr";

export function Divers(): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mainColor =
        useSelector((s: RootState) => s.global.infoUser.maincolor) ||
        DEFAULT_MAIN_COLOR;

    const reduxLangues = useSelector(
        (s: RootState) => s.servio.langues
    ) as Partial<DiversState>;

    const state: DiversState = React.useMemo(
        () => ({
            enabledLanguages: {
                ...DEFAULT_ENABLED,
                ...(reduxLangues.enabledLanguages || {}),
            },
            defaultLanguage: reduxLangues.defaultLanguage || DEFAULT_LANGUAGE,
        }),
        [reduxLangues]
    );

    const handleToggleLanguage = (code: string): void => {
        const isCurrentlyEnabled = state.enabledLanguages[code];
        const enabledCount = Object.values(state.enabledLanguages).filter(
            Boolean
        ).length;
        // Don't allow disabling the last enabled language.
        if (isCurrentlyEnabled && enabledCount <= 1) return;

        const updated = {
            ...state.enabledLanguages,
            [code]: !isCurrentlyEnabled,
        };

        // If we just disabled the current default, fall back to whatever
        // is still enabled.
        let newDefault = state.defaultLanguage;
        if (isCurrentlyEnabled && state.defaultLanguage === code) {
            const firstEnabled = Object.entries(updated).find(
                ([, enabled]) => enabled
            );
            if (firstEnabled) newDefault = firstEnabled[0];
        }

        dispatch(
            setServio({
                langues: {
                    enabledLanguages: updated,
                    defaultLanguage: newDefault,
                },
            })
        );
    };

    const handleSetDefaultLanguage = (code: string): void => {
        if (!state.enabledLanguages[code]) {
            // Auto-enable a language when the user picks it as default.
            dispatch(
                setServio({
                    langues: {
                        enabledLanguages: {
                            ...state.enabledLanguages,
                            [code]: true,
                        },
                        defaultLanguage: code,
                    },
                })
            );
            return;
        }
        dispatch(
            setServio({
                langues: {
                    enabledLanguages: state.enabledLanguages,
                    defaultLanguage: code,
                },
            })
        );
    };

    const resetLanguages = (): void => {
        dispatch(
            setServio({
                langues: {
                    enabledLanguages: { ...DEFAULT_ENABLED },
                    defaultLanguage: DEFAULT_LANGUAGE,
                },
            })
        );
    };

    return (
        <Container data-testid="servio-langues-tab">
            <div>
                <SectionTitle>{t("Languages")}</SectionTitle>
            </div>

            <LanguagesCard
                enabledLanguages={state.enabledLanguages}
                defaultLanguage={state.defaultLanguage}
                mainColor={mainColor}
                onToggleLanguage={handleToggleLanguage}
                onSetDefault={handleSetDefaultLanguage}
                onReset={resetLanguages}
            />
        </Container>
    );
}
